<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto px-4">

      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">Situação de Frota</h1>
            <p class="text-sm text-slate-500 font-medium mt-1">Histórico de disponibilidade e manutenção de veículos</p>
          </div>
        </div>
      </div>

      <!-- FILTRO -->
      <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm mb-6">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-magnify</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">FILTRAR CONSULTA</h2>
          </div>
          <button
            @click="$router.push('/vehicles-manager/situacao/create')"
            style="height: 32px; width: auto; white-space: nowrap; flex-shrink: 0; background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 0 14px; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border: none; cursor: pointer; transition: background-color 0.2s;"
            onmouseover="this.style.backgroundColor='#e0601a'"
            onmouseout="this.style.backgroundColor='#f37021'"
          >
            <v-icon color="white" x-small>mdi-plus</v-icon>
            NOVA SITUAÇÃO
          </button>
        </div>

        <div class="p-8">
          <v-row align="end">
            <v-col cols="12" md="4">
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">MÊS E ANO</label>
              <v-text-field
                v-model="filter.date"
                type="month"
                solo flat dense hide-details
                class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main"
                height="48"
              />
            </v-col>

            <v-col v-if="isAdmin" cols="12" md="5">
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">PREFEITURA</label>
              <v-autocomplete
                v-model="filter.prefecture_id"
                :items="prefectures"
                item-text="prefecture_social_name"
                item-value="prefecture_id"
                placeholder="Selecione a Prefeitura..."
                solo flat dense hide-details
                class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main"
                height="48"
              />
            </v-col>

            <!-- Prefeitura pré-selecionada (não-admin): exibe nome bloqueado -->
            <v-col v-if="!isAdmin" cols="12" md="5">
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">PREFEITURA</label>
              <div class="bg-slate-100 border border-slate-200 rounded-lg px-4 flex items-center" style="height:48px;">
                <v-icon size="16" color="#94a3b8" class="mr-2">mdi-domain</v-icon>
                <span class="text-sm font-bold text-slate-600 uppercase tracking-wide">{{ prefeituraFixaNome }}</span>
                <v-icon size="14" color="#94a3b8" class="ml-auto">mdi-lock-outline</v-icon>
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <button
                @click="applyFilter"
                :disabled="!(filter.prefecture_id && filter.date) || loader"
                class="w-full font-black text-[11px] h-[48px] rounded-lg transition-all uppercase tracking-widest"
                :class="(filter.prefecture_id && filter.date) ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md' : 'bg-[#f1f5f9] text-slate-400 opacity-60 cursor-not-allowed'"
              >
                {{ loader ? 'CARREGANDO...' : 'FILTRAR' }}
              </button>
            </v-col>
          </v-row>
        </div>
      </section>

      <!-- DASHBOARD KPI CARDS -->
      <transition name="fade-slide">
        <section v-if="dashboard.loaded" class="mb-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

            <!-- Card: Veículos Ativos -->
            <div class="kpi-card kpi-green">
              <div class="kpi-icon-wrap kpi-icon-green">
                <v-icon color="white" size="22">mdi-car-multiple</v-icon>
              </div>
              <div class="kpi-body">
                <p class="kpi-label">Veículos Ativos</p>
                <p class="kpi-value kpi-value-green">{{ dashboard.totalAtivos }}</p>
              </div>
              <div class="kpi-bar kpi-bar-green"></div>
            </div>

            <!-- Card: Manutenções Realizadas -->
            <div class="kpi-card kpi-orange">
              <div class="kpi-icon-wrap kpi-icon-orange">
                <v-icon color="white" size="22">mdi-wrench-clock</v-icon>
              </div>
              <div class="kpi-body">
                <p class="kpi-label">Manutenções Realizadas</p>
                <p class="kpi-value kpi-value-orange">{{ dashboard.manutencoesRealizadas }}</p>
              </div>
              <div class="kpi-bar kpi-bar-orange"></div>
            </div>

            <!-- Card: Em Manutenção -->
            <div class="kpi-card kpi-yellow">
              <div class="kpi-icon-wrap kpi-icon-yellow">
                <v-icon color="white" size="22">mdi-tools</v-icon>
              </div>
              <div class="kpi-body">
                <p class="kpi-label">Em Manutenção</p>
                <p class="kpi-value kpi-value-yellow">{{ dashboard.emManutencao }}</p>
              </div>
              <div class="kpi-bar kpi-bar-yellow"></div>
            </div>

            <!-- Card: Baixados -->
            <div class="kpi-card kpi-red">
              <div class="kpi-icon-wrap kpi-icon-red">
                <v-icon color="white" size="22">mdi-car-off</v-icon>
              </div>
              <div class="kpi-body">
                <p class="kpi-label">Baixados</p>
                <p class="kpi-value kpi-value-red">{{ dashboard.baixados }}</p>
              </div>
              <div class="kpi-bar kpi-bar-red"></div>
            </div>

            <!-- Card: Baixa Provisória -->
            <div class="kpi-card kpi-amber">
              <div class="kpi-icon-wrap kpi-icon-amber">
                <v-icon color="white" size="22">mdi-car-clock</v-icon>
              </div>
              <div class="kpi-body">
                <p class="kpi-label">Baixa Provisória</p>
                <p class="kpi-value kpi-value-amber">{{ dashboard.baixaProvisoria }}</p>
              </div>
              <div class="kpi-bar kpi-bar-amber"></div>
            </div>

          </div>
        </section>
      </transition>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <!-- BARRA DE TÍTULO DA TABELA -->
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-history</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">HISTÓRICO DE SITUAÇÕES</h2>
          </div>

          <div class="flex items-center gap-4">
            <v-btn icon dark small @click="showSearch = !showSearch">
              <v-icon size="18">mdi-magnify</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- SEARCH OVERLAY -->
        <div v-if="showSearch" class="px-8 py-4 bg-slate-800 border-b border-slate-700">
          <v-text-field
            v-model="search"
            placeholder="PESQUISAR NO HISTÓRICO..."
            solo flat dense hide-details
            class="custom-field-dark bg-white/5 rounded-xl"
            prepend-inner-icon="mdi-magnify"
            append-icon="mdi-close"
            @click:append="showSearch = false"
          />
        </div>

        <v-data-table
          :headers="headers"
          :items="data"
          :search="search"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [15, 30, 50, -1],
            'items-per-page-text': 'Registros por página:'
          }"
          :loading="loader"
          loading-text="Sincronizando histórico..."
          class="custom-table"
          group-by="veiculo"
          :show-group-by="false"
        >
          <template v-slot:group.header="{ group, headers, toggle, isOpen }">
            <td :colspan="headers.length" class="bg-slate-50/80 py-3 px-6 cursor-pointer font-black text-slate-700 text-xs uppercase" @click="toggle" style="border-bottom: 1px solid #e2e8f0;">
              <v-icon small class="mr-2 text-slate-500">{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              <span>{{ group }}</span>
            </td>
          </template>

          <template v-slot:item.veiculo>
            <div class="flex items-center pl-4 text-slate-400">
              <v-icon x-small color="currentColor">mdi-subdirectory-arrow-right</v-icon>
            </div>
          </template>

          <template v-slot:item.data="{ item }">
            <span class="text-xs font-bold text-slate-500">{{ item.data }}</span>
          </template>

          <template v-slot:item.situacao="{ item }">
            <div class="flex justify-center">
              <span
                :class="[
                  'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border',
                  item.situacao == 1 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                  item.situacao == 2 ? 'bg-orange-50 text-orange-600 border-orange-100' :
                  item.situacao == 4 ? 'bg-amber-50 text-amber-600 border-amber-100' :
                  'bg-rose-50 text-rose-600 border-rose-100'
                ]"
              >
                {{ item.situacao == 1 ? 'Em Uso' : item.situacao == 2 ? 'Em Manutenção' : item.situacao == 4 ? 'Baixa Provisória' : 'Baixada' }}
              </span>
            </div>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="flex justify-end pr-4">
              <v-btn icon small class="hover:bg-rose-50 group" @click="deleteItem(item.id)">
                <v-icon size="18" color="rose-300" class="group-hover:text-rose-500 transition-colors">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="py-20 flex flex-col items-center justify-center text-slate-300">
              <v-icon size="48" color="slate-100">mdi-database-off-outline</v-icon>
              <p class="text-[10px] font-black uppercase tracking-[0.2em] mt-4">Nenhum registro localizado</p>
            </div>
          </template>
        </v-data-table>
      </section>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      prefectures: [],
      filter: {
        prefecture_id: null,
        date: null,
      },
      dashboard: {
        loaded: false,
        totalAtivos: 0,
        manutencoesRealizadas: 0,
        emManutencao: 0,
        baixados: 0,
        baixaProvisoria: 0,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Veículo/Máquina", value: "veiculo" },
        { text: "Data", value: "data", align: "center" },
        { text: "Situação", value: "situacao", align: "center" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    isAdmin() {
      const userData = this.$store.getters.getUserData;
      // group_id === 1 é o administrador
      const groupId = this.$store.state.userData && this.$store.state.userData.group_id;
      return groupId === 1 || (userData && userData.group_id === 1);
    },
    prefeituraFixaNome() {
      if (!this.filter.prefecture_id || !this.prefectures.length) return '';
      const found = this.prefectures.find(p => p.prefecture_id === this.filter.prefecture_id);
      return found ? found.prefecture_social_name : '';
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;

        // Se usuário é de prefeitura: pré-selecionar e já carregar com o mês atual
        const userData = this.$store.getters.getUserData;
        if (userData && userData.table_key === 'prefectures' && userData.table_id) {
          this.filter.prefecture_id = userData.table_id;

          // Pré-preencher com o mês atual
          if (!this.filter.date) {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            this.filter.date = `${y}-${m}`;
          }
        }

        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },

    applyFilter() {
      if (!this.filter.prefecture_id || !this.filter.date) return;
      this.loadSituacao();
      this.loadDashboard();
    },

    loadDashboard() {
      const year = this.filter.date.substring(0, 4);
      const month = this.filter.date.substring(5);
      this.$http
        .get(`/vehicles/situacao/dashboard/${this.filter.prefecture_id}/${year}/${month}`)
        .then((r) => {
          this.dashboard = {
            loaded: true,
            totalAtivos: r.data.totalAtivos || 0,
            manutencoesRealizadas: r.data.manutencoesRealizadas || 0,
            emManutencao: r.data.emManutencao || 0,
            baixados: r.data.baixados || 0,
            baixaProvisoria: r.data.baixaProvisoria || 0,
          };
        })
        .catch(() => {
          this.dashboard.loaded = false;
        });
    },

    loadSituacao() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(
          `/vehicles/situacao/all/${this.filter.prefecture_id}/${this.filter.date.substring(0, 4)}/${this.filter.date.substring(5)}`
        )
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },

    deleteItem(id) {
      if (confirm("Deseja excluir o registro?")) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/vehicles/situacao/${id}`)
          .then(() => {
            this.snack.show = true;
            this.snack.message = "Excluido com sucesso!";
            this.snack.color = "success";
            this.applyFilter();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.sci-page-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 40px 20px;
}

/* ──────────────────────────────────────────
   KPI CARDS
────────────────────────────────────────── */
.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 20px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.10);
}

.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-body {
  flex: 1;
}

.kpi-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 2.2rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.03em;
}

.kpi-bar {
  height: 4px;
  border-radius: 0 0 0 0;
  margin: 8px -20px 0 -20px;
}

/* Verde — Ativos */
.kpi-icon-green  { background: #10b981; }
.kpi-value-green { color: #059669; }
.kpi-bar-green   { background: linear-gradient(90deg, #10b981, #34d399); }

/* Laranja — Manutenções Realizadas */
.kpi-icon-orange  { background: #f37021; }
.kpi-value-orange { color: #ea6b14; }
.kpi-bar-orange   { background: linear-gradient(90deg, #f37021, #fb923c); }

/* Amarelo — Em Manutenção */
.kpi-icon-yellow  { background: #eab308; }
.kpi-value-yellow { color: #ca8a04; }
.kpi-bar-yellow   { background: linear-gradient(90deg, #eab308, #facc15); }

/* Vermelho — Baixados */
.kpi-icon-red  { background: #ef4444; }
.kpi-value-red { color: #dc2626; }
.kpi-bar-red   { background: linear-gradient(90deg, #ef4444, #f87171); }

/* Âmbar — Baixa Provisória */
.kpi-icon-amber  { background: #f59e0b; }
.kpi-value-amber { color: #d97706; }
.kpi-bar-amber   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

/* ──────────────────────────────────────────
   TRANSITIONS
────────────────────────────────────────── */
.fade-slide-enter-active {
  transition: all 0.35s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(-10px);
}

/* ──────────────────────────────────────────
   FILTRO
────────────────────────────────────────── */
.custom-select-main >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 16px !important;
}

.custom-select-main >>> input,
.custom-select-main >>> .v-select__selection {
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem;
  color: #334155 !important;
}

/* ──────────────────────────────────────────
   TABELA
────────────────────────────────────────── */
.custom-table ::v-deep table { background-color: transparent !important; }
.custom-table ::v-deep th {
  color: #64748b !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  font-size: 0.7rem !important;
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 56px !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep td {
  border-bottom: 1px solid #f1f5f9 !important;
  height: 64px !important;
  padding: 0 24px !important;
}

.custom-field-dark >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 16px !important;
}
.custom-field-dark >>> input {
  color: #ffffff !important;
  text-transform: uppercase;
  font-weight: 600;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 12px !important;
}
</style>
